import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { ArrowRight, Star, Shield, Truck } from 'lucide-react';
import { Button } from '../components/ui/button';
import ProductCard from '../components/ProductCard';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Home = () => {
  const [featuredProducts, setFeaturedProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchFeatured = async () => {
      try {
        const response = await axios.get(`${API}/products/featured`);
        setFeaturedProducts(response.data.products);
      } catch (error) {
        console.error('Failed to fetch featured products:', error);
      } finally {
        setLoading(false);
      }
    };
    
    // Seed database first, then fetch
    const seedAndFetch = async () => {
      try {
        await axios.post(`${API}/seed`);
      } catch (e) {
        // Already seeded, ignore
      }
      fetchFeatured();
    };
    
    seedAndFetch();
  }, []);

  return (
    <div data-testid="home-page">
      {/* Hero Section */}
      <section className="relative min-h-[90vh] bg-deep-obsidian overflow-hidden">
        {/* Background Image */}
        <div className="absolute inset-0">
          <img
            src="https://images.unsplash.com/photo-1765036464882-e36092ba4bb4?w=1920&q=80"
            alt="Basketball court"
            className="w-full h-full object-cover opacity-40"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-deep-obsidian via-deep-obsidian/80 to-transparent" />
        </div>

        {/* Content */}
        <div className="relative max-w-7xl mx-auto px-6 min-h-[90vh] flex items-center">
          <div className="max-w-2xl">
            <div className="overflow-hidden">
              <p className="font-bebas text-chicago-red text-xl tracking-widest animate-slide-up">
                AUTHENTIC VINTAGE COLLECTION
              </p>
            </div>
            <div className="overflow-hidden mt-4">
              <h1 className="font-anton text-5xl md:text-7xl lg:text-8xl text-white tracking-tighter uppercase animate-slide-up-delay-1">
                RARE KICKS
              </h1>
            </div>
            <div className="overflow-hidden">
              <h1 className="font-anton text-5xl md:text-7xl lg:text-8xl text-white tracking-tighter uppercase animate-slide-up-delay-2">
                FROM THE
                <span className="text-chicago-red"> GOLDEN ERA</span>
              </h1>
            </div>
            <div className="overflow-hidden mt-6">
              <p className="text-cement-grey text-lg max-w-md animate-slide-up-delay-3">
                Curating original basketball shoes from the 90s and 2000s. 
                Every pair authenticated. Every story preserved.
              </p>
            </div>
            <div className="mt-10 flex flex-wrap gap-4 animate-slide-up-delay-3">
              <Link to="/products">
                <Button 
                  className="bg-chicago-red hover:bg-chicago-red/90 text-white uppercase tracking-widest text-sm font-bold py-6 px-10 rounded-sm btn-primary"
                  data-testid="hero-shop-btn"
                >
                  Shop Collection
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </Link>
              <Link to="/products?category=Jordan">
                <Button 
                  variant="outline"
                  className="border-2 border-white text-white hover:bg-white hover:text-deep-obsidian uppercase tracking-widest text-sm font-bold py-6 px-10 rounded-sm"
                >
                  Explore Jordans
                </Button>
              </Link>
            </div>
          </div>
        </div>

        {/* Scroll indicator */}
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center text-white/50">
          <span className="text-xs uppercase tracking-widest mb-2">Scroll</span>
          <div className="w-px h-8 bg-white/30 relative overflow-hidden">
            <div className="absolute top-0 left-0 w-full h-full bg-chicago-red animate-pulse" />
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="py-16 bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="flex items-center gap-4 p-6">
              <div className="w-16 h-16 bg-deep-obsidian flex items-center justify-center">
                <Shield className="w-8 h-8 text-chicago-red" />
              </div>
              <div>
                <h3 className="font-bebas text-xl">100% AUTHENTIC</h3>
                <p className="text-cement-grey text-sm">Every pair verified by experts</p>
              </div>
            </div>
            <div className="flex items-center gap-4 p-6">
              <div className="w-16 h-16 bg-deep-obsidian flex items-center justify-center">
                <Truck className="w-8 h-8 text-chicago-red" />
              </div>
              <div>
                <h3 className="font-bebas text-xl">WORLDWIDE SHIPPING</h3>
                <p className="text-cement-grey text-sm">Insured delivery to your door</p>
              </div>
            </div>
            <div className="flex items-center gap-4 p-6">
              <div className="w-16 h-16 bg-deep-obsidian flex items-center justify-center">
                <Star className="w-8 h-8 text-chicago-red" />
              </div>
              <div>
                <h3 className="font-bebas text-xl">PREMIUM QUALITY</h3>
                <p className="text-cement-grey text-sm">Condition graded collection</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Featured Products */}
      <section className="py-24 bg-concrete-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex items-end justify-between mb-12">
            <div>
              <p className="font-bebas text-chicago-red text-lg tracking-widest">HANDPICKED</p>
              <h2 className="font-anton text-4xl md:text-5xl tracking-tighter uppercase">FEATURED DROPS</h2>
            </div>
            <Link 
              to="/products" 
              className="hidden md:flex items-center gap-2 text-deep-obsidian font-bold uppercase tracking-widest text-sm hover:text-chicago-red transition-colors"
            >
              View All
              <ArrowRight className="w-4 h-4" />
            </Link>
          </div>

          {loading ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {[...Array(4)].map((_, i) => (
                <div key={i} className="bg-white animate-pulse">
                  <div className="aspect-square bg-gray-200" />
                  <div className="p-4 space-y-3">
                    <div className="h-4 bg-gray-200 rounded w-1/4" />
                    <div className="h-5 bg-gray-200 rounded w-3/4" />
                    <div className="h-6 bg-gray-200 rounded w-1/3" />
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6" data-testid="featured-products">
              {featuredProducts.slice(0, 4).map((product) => (
                <ProductCard key={product.id} product={product} />
              ))}
            </div>
          )}

          <div className="mt-12 text-center md:hidden">
            <Link to="/products">
              <Button className="bg-deep-obsidian hover:bg-deep-obsidian/90 text-white uppercase tracking-widest text-sm font-bold py-4 px-8 rounded-sm">
                View All Products
                <ArrowRight className="ml-2 w-4 h-4" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Era Sections */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {/* 90s Era */}
            <Link 
              to="/products?era=1990s"
              className="group relative h-[500px] overflow-hidden"
              data-testid="era-90s"
            >
              <img
                src="https://images.unsplash.com/photo-1635949602587-7c970c33ffb3?w=800&q=80"
                alt="90s Era"
                className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-deep-obsidian via-deep-obsidian/50 to-transparent" />
              <div className="absolute bottom-0 left-0 p-8">
                <p className="font-bebas text-chicago-red text-lg tracking-widest">THE DYNASTY</p>
                <h3 className="font-anton text-4xl md:text-5xl text-white tracking-tighter uppercase">
                  90S ERA
                </h3>
                <p className="text-cement-grey mt-2 max-w-sm">
                  The golden age of basketball footwear. Jordan's reign, Penny's style, Shaq's dominance.
                </p>
                <span className="inline-flex items-center gap-2 text-white font-bold uppercase tracking-widest text-sm mt-4 group-hover:text-chicago-red transition-colors">
                  Explore Collection
                  <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                </span>
              </div>
            </Link>

            {/* 2000s Era */}
            <Link 
              to="/products?era=2000s"
              className="group relative h-[500px] overflow-hidden"
              data-testid="era-2000s"
            >
              <img
                src="https://images.unsplash.com/photo-1746456994470-7b0302ebffa4?w=800&q=80"
                alt="2000s Era"
                className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-deep-obsidian via-deep-obsidian/50 to-transparent" />
              <div className="absolute bottom-0 left-0 p-8">
                <p className="font-bebas text-chicago-red text-lg tracking-widest">THE EVOLUTION</p>
                <h3 className="font-anton text-4xl md:text-5xl text-white tracking-tighter uppercase">
                  2000S ERA
                </h3>
                <p className="text-cement-grey mt-2 max-w-sm">
                  Innovation meets style. Vince Carter's dunks, Kobe's mamba mentality, LeBron's rise.
                </p>
                <span className="inline-flex items-center gap-2 text-white font-bold uppercase tracking-widest text-sm mt-4 group-hover:text-chicago-red transition-colors">
                  Explore Collection
                  <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                </span>
              </div>
            </Link>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-deep-obsidian relative overflow-hidden">
        <div className="absolute inset-0 grain-overlay" />
        <div className="relative max-w-7xl mx-auto px-6 text-center">
          <p className="font-bebas text-chicago-red text-xl tracking-widest">JOIN THE COLLECTORS</p>
          <h2 className="font-anton text-4xl md:text-6xl text-white tracking-tighter uppercase mt-4">
            STEP INTO HISTORY
          </h2>
          <p className="text-cement-grey mt-6 max-w-2xl mx-auto text-lg">
            Create an account to save your favorites, track orders, and get exclusive access to rare drops before anyone else.
          </p>
          <div className="mt-10">
            <Link to="/signup">
              <Button 
                className="bg-chicago-red hover:bg-chicago-red/90 text-white uppercase tracking-widest text-sm font-bold py-6 px-12 rounded-sm btn-primary"
                data-testid="cta-signup-btn"
              >
                Create Account
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
